import {CashbackRateConfig, CashbackRateType, CashbackSettings} from "../../resources/depositProduct"

export const CASHBACK_MVP_RATE_BPS = 100
export const CASHBACK_MVP_MONTHLY_CAP_CENTS = 50_000
export const CASHBACK_MVP_RATE_PERCENT = CASHBACK_MVP_RATE_BPS / 100

export function bpsToCashbackRatePercent(bps: number): number {
	return bps / 100
}

export function cashbackRatePercentToBps(percent: number): number {
	return percent * 100
}

export function getCashbackRateType(rateConfig: CashbackRateConfig): CashbackRateType {
	if ("Percentage" in rateConfig) {
		return "Percentage"
	}
	return "Fixed"
}

export function getCashbackRateBps(rateConfig: CashbackRateConfig): number | undefined {
	if ("Percentage" in rateConfig) {
		return rateConfig.Percentage.rate
	}
	return undefined
}

export function getCashbackFixedAmountCents(rateConfig: CashbackRateConfig): number | undefined {
	if ("Fixed" in rateConfig) {
		return rateConfig.Fixed.amountCents
	}
	return undefined
}

export function formatCashbackPercentageRate(rateBps: number): string {
	return `${(rateBps / 100).toFixed(2)}%`
}

export function formatCashbackRateDisplay(rateConfig: CashbackRateConfig): string {
	if ("Percentage" in rateConfig) {
		return `${formatCashbackPercentageRate(rateConfig.Percentage.rate)} (${rateConfig.Percentage.rate} bps)`
	}
	return `$${(rateConfig.Fixed.amountCents / 100).toFixed(2)}`
}

export type CashbackFormValues = {
	cashbackEnabled?: boolean
	cashbackRateType?: CashbackRateType
	cashbackRate?: number
	cashbackFixedAmountCents?: number
	cashbackMonthlyCapCents?: number
	cashbackMinMonthlySpendCents?: number | null
	cashbackFundingAccountId?: string
	cashbackUnitShare?: number
}

export type CashbackSettingsPatch =
	| {action: "set"; value: CashbackSettings}
	| {action: "delete"}
	| {action: "unspecified"}

export function cashbackSettingsEqual(a: CashbackSettings, b: CashbackSettings): boolean {
	if (
		a.monthlyCapCents !== b.monthlyCapCents ||
		a.minMonthlySpendCents !== b.minMonthlySpendCents ||
		a.fundingAccountId !== b.fundingAccountId ||
		a.unitShare !== b.unitShare
	) {
		return false
	}

	const aType = getCashbackRateType(a.rateConfig)
	const bType = getCashbackRateType(b.rateConfig)
	if (aType !== bType) {
		return false
	}

	if (aType === "Percentage") {
		return getCashbackRateBps(a.rateConfig) === getCashbackRateBps(b.rateConfig)
	}

	return getCashbackFixedAmountCents(a.rateConfig) === getCashbackFixedAmountCents(b.rateConfig)
}

export function cashbackFormValuesFromSettings(settings?: CashbackSettings): CashbackFormValues {
	if (!settings) {
		return {
			cashbackEnabled: false,
			cashbackRateType: "Percentage",
			cashbackRate: CASHBACK_MVP_RATE_BPS,
			cashbackMonthlyCapCents: CASHBACK_MVP_MONTHLY_CAP_CENTS,
		}
	}

	const rateType = getCashbackRateType(settings.rateConfig)

	return {
		cashbackEnabled: true,
		cashbackRateType: rateType,
		cashbackRate: getCashbackRateBps(settings.rateConfig),
		cashbackFixedAmountCents: getCashbackFixedAmountCents(settings.rateConfig),
		cashbackMonthlyCapCents: settings.monthlyCapCents,
		cashbackMinMonthlySpendCents: settings.minMonthlySpendCents,
		cashbackFundingAccountId: settings.fundingAccountId,
		cashbackUnitShare: settings.unitShare,
	}
}

export function buildCashbackSettingsPatch(
	originalSettings: CashbackSettings | undefined,
	values: CashbackFormValues
): CashbackSettingsPatch {
	const hadOriginal = originalSettings !== undefined
	const cashbackEnabled = values.cashbackEnabled ?? false

	if (!cashbackEnabled) {
		if (hadOriginal) {
			return {action: "delete"}
		}
		return {action: "unspecified"}
	}

	const settings = buildCashbackSettingsFromFormValues(values)
	if (!settings) {
		return {action: "unspecified"}
	}

	if (hadOriginal && cashbackSettingsEqual(originalSettings, settings)) {
		return {action: "unspecified"}
	}

	return {action: "set", value: settings}
}

export function cashbackSettingsPatchToAttributes(patch: CashbackSettingsPatch): {
	cashbackSettings?: CashbackSettings | null
} {
	switch (patch.action) {
		case "set":
			return {cashbackSettings: patch.value}
		case "delete":
			return {cashbackSettings: null}
		case "unspecified":
			return {}
	}
}

export function buildCashbackSettingsFromFormValues(values: CashbackFormValues): CashbackSettings | undefined {
	const {
		cashbackRateType,
		cashbackRate,
		cashbackFixedAmountCents,
		cashbackMonthlyCapCents,
		cashbackMinMonthlySpendCents,
		cashbackFundingAccountId,
		cashbackUnitShare,
	} = values

	if (
		cashbackFundingAccountId === undefined ||
		cashbackFundingAccountId === "" ||
		cashbackMonthlyCapCents === undefined ||
		cashbackUnitShare === undefined
	) {
		return undefined
	}

	const rateType = cashbackRateType ?? "Percentage"

	if (rateType === "Percentage") {
		if (cashbackRate === undefined) {
			return undefined
		}

		return {
			rateConfig: {Percentage: {rate: cashbackRate}},
			monthlyCapCents: cashbackMonthlyCapCents,
			minMonthlySpendCents: cashbackMinMonthlySpendCents,
			fundingAccountId: cashbackFundingAccountId,
			unitShare: cashbackUnitShare,
		}
	}

	if (cashbackFixedAmountCents === undefined) {
		return undefined
	}

	return {
		rateConfig: {Fixed: {amountCents: cashbackFixedAmountCents}},
		monthlyCapCents: cashbackMonthlyCapCents,
		minMonthlySpendCents: cashbackMinMonthlySpendCents,
		fundingAccountId: cashbackFundingAccountId,
		unitShare: cashbackUnitShare,
	}
}
